/**
 * The frame every authenticated screen renders inside.
 *
 * It owns three things and nothing else:
 *   1. the layout — rail, top bar, one scroll container for the screen;
 *   2. the global keymap (build contract §7), so no screen has to re-declare it;
 *   3. the two global overlays, ⌘K and `?`.
 *
 * Where each key is bound:
 *   ⌘K · / · ? · ⌘\ · ⌘. · ⌘⇧L   here
 *   g …                          here
 *   j k x ↵ e a s l n            the screen that owns the list or record
 */

import { Suspense, useCallback, useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { CommandPalette, COMMAND_PALETTE_EVENT } from '@/components/app/CommandPalette';
import {
  AnalyticsScreenSkeleton,
  BoardScreenSkeleton,
  GettingStartedSkeleton,
  RecordDetailSkeleton,
  SettingsScreenSkeleton,
  TasksScreenSkeleton,
} from '@/components/app/RouteSkeletons';
import { ShortcutSheet, SHORTCUT_SHEET_EVENT } from '@/components/app/ShortcutSheet';
import { NavDrawer, Sidebar, SidebarSkeleton } from '@/components/app/Sidebar';
import { Topbar, TopbarSkeleton } from '@/components/app/Topbar';
import { TourOverlay } from '@/features/onboarding/TourOverlay';
import { useOnboardingTracking } from '@/features/onboarding/useOnboarding';
import { Skeleton } from '@/components/ui';
import { UNAUTHORIZED_EVENT } from '@/lib/api';
import { useBugKit } from '@/lib/bugkit';
import { useCreate, useCreateHost } from '@/lib/create';
import { ROUTES } from '@/lib/constants';
import { ANY_SCOPE, useShortcut } from '@/lib/keyboard';
import { queryKeys, useLivePipeline, useSession, useStages } from '@/lib/queries';
import { focusScopedSearch } from '@/lib/scopedSearch';
import { useTheme } from '@/lib/theme';
import { useLocalStorage } from '@/lib/useLocalStorage';
import { useIsMobile } from '@/lib/viewport';

/* ────────────────────────────────────────────────────────────────────────────
   Shared shell state
   ──────────────────────────────────────────────────────────────────────────── */

export const SIDEBAR_STORAGE_KEY = 'af.sidebar.collapsed';

/**
 * `⌘.` toggles the record detail pane. The shell does not own that pane, so it
 * persists the preference and announces the change; the screen that owns the
 * pane reads this key and listens for this event.
 */
export const DETAIL_PANE_STORAGE_KEY = 'af.detail.open';
export const DETAIL_PANE_EVENT = 'af:detail-pane';

/* ────────────────────────────────────────────────────────────────────────────
   The global create — see `@/lib/create`
   ──────────────────────────────────────────────────────────────────────────── */

/*
 * It used to live here. It is in `lib/create.ts` now because FOUR doors open it
 * — this shell's `New` menu, ⌘K's Create group, the sidebar's `+` beside Saved
 * views and each screen's own gold primary — and a module every one of them can
 * import without importing the shell is what keeps them opening the same thing.
 */
export { CREATE_EVENT, useCreateRequest } from '@/lib/create';
export type { CreateEventDetail } from '@/lib/create';

/* ────────────────────────────────────────────────────────────────────────────
   Shell
   ──────────────────────────────────────────────────────────────────────────── */

export function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { toggle: toggleTheme } = useTheme();
  const { data: session } = useSession();

  /*
    The pipeline, fetched ONCE for the whole application.

    Every surface resolves a stage's label and its `--stage-*` step through
    `stageMeta()`, which reads a registry `useStages` publishes. Mounting the
    query here means that registry is warm before any screen renders, so the
    command palette, a contact record and a company record — none of which
    fetch a pipeline of their own — spell and paint a stage exactly the way the
    board and the inbox do. `useLivePipeline` repaints the tree the one time the
    server's stages replace the seeded ones.
  */
  useStages();
  useLivePipeline();

  const [collapsed, setCollapsed] = useLocalStorage<boolean>(SIDEBAR_STORAGE_KEY, false);
  const [detailOpen, setDetailOpen] = useLocalStorage<boolean>(DETAIL_PANE_STORAGE_KEY, true);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  /*
    Below the breakpoint the rail is a drawer, so the one control in the top bar
    means two different things and `⌘\` has to follow it: on a phone there is no
    56px collapsed state worth persisting — the choice is "is it over the screen
    or not" — and on a desktop the drawer does not exist. `mobile` gates which
    piece of state the toggle writes, and the drawer's own open state is NOT
    persisted: a panel that covers the reading surface must never be what a
    reload restores.
  */
  const mobile = useIsMobile();
  const [navOpen, setNavOpen] = useState(false);

  const toggleSidebar = useCallback(() => {
    if (mobile) setNavOpen((value) => !value);
    else setCollapsed((value) => !value);
  }, [mobile, setCollapsed]);

  const create = useCreate();
  /* The shell outlives every navigation, so it is where a parked create waits
     for the code-split screen that owns it. See `@/lib/create`. */
  useCreateHost();

  /*
    The onboarding checklist fills itself in while you work — including on the
    days you never open it — so the thing that watches where you have been is
    mounted HERE rather than on the Getting started screen. One listener, every
    navigation. See `features/onboarding/useOnboarding`.
  */
  useOnboardingTracking();

  /*
    The bug reporter's triggers — the floating 🐞 and ⌃⌥B — are mounted here and
    not in `index.html`, because the shell is the first thing that renders only
    behind the session guard: on the sign-in screen a report could not be
    attributed to anyone and a submit could only 401.

    The widget itself is already loaded and already recording by this point. It
    is a plain script in the document head, ahead of the bundle, so a report
    filed after a boot failure still carries the boot failure in its console
    buffer. See `@/lib/bugkit`.
  */
  useBugKit(session?.user.id ?? null, session?.user.role ?? null);

  const toggleDetail = useCallback(() => {
    const next = !detailOpen;
    setDetailOpen(next);
    window.dispatchEvent(new CustomEvent(DETAIL_PANE_EVENT, { detail: { open: next } }));
  }, [detailOpen, setDetailOpen]);

  /* ── global keymap ───────────────────────────────────────────────────── */

  useShortcut('palette', () => setPaletteOpen((value) => !value), {
    scope: ANY_SCOPE,
    allowInInput: true,
  });
  /* `/` belongs to the surface's own search field when there is one — the
     toolbar prints a `/` chip on it and announces `aria-keyshortcuts="/"`, and
     both were false while this line unconditionally opened the palette. The
     palette keeps the key on surfaces that have no field of their own. */
  useShortcut('search', () => {
    if (focusScopedSearch()) return;
    setPaletteOpen(true);
  });
  useShortcut('help', () => setHelpOpen((value) => !value));
  useShortcut('toggleSidebar', toggleSidebar, { scope: ANY_SCOPE, allowInInput: true });
  useShortcut('toggleDetail', toggleDetail, { scope: ANY_SCOPE, allowInInput: true });
  useShortcut('toggleTheme', toggleTheme, { scope: ANY_SCOPE, allowInInput: true });

  useShortcut('goInbox', () => navigate(ROUTES.inbox));
  useShortcut('goBoard', () => navigate(ROUTES.board));
  useShortcut('goContacts', () => navigate(ROUTES.contacts));
  useShortcut('goAnalytics', () => navigate(ROUTES.analytics));
  useShortcut('goTasks', () => navigate(ROUTES.tasks));
  useShortcut('goSettings', () => navigate(ROUTES.settings));

  /* ── imperative opens from menus and commands ────────────────────────── */

  useEffect(() => {
    const openPalette = () => setPaletteOpen(true);
    const openHelp = () => setHelpOpen(true);
    window.addEventListener(COMMAND_PALETTE_EVENT, openPalette);
    window.addEventListener(SHORTCUT_SHEET_EVENT, openHelp);
    return () => {
      window.removeEventListener(COMMAND_PALETTE_EVENT, openPalette);
      window.removeEventListener(SHORTCUT_SHEET_EVENT, openHelp);
    };
  }, []);

  /* ── session expiry ──────────────────────────────────────────────────── */

  useEffect(() => {
    const onUnauthorized = () => {
      queryClient.removeQueries({ queryKey: queryKeys.session() });
      navigate(ROUTES.login, {
        replace: true,
        state: { from: `${location.pathname}${location.search}` },
      });
    };
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, [navigate, queryClient, location.pathname, location.search]);

  return (
    <div className="flex h-full w-full overflow-hidden bg-[var(--canvas)]">
      <a
        href="#af-main"
        // The link is stop 0; it has to land on the thing the screen is *about*,
        // not on the region that contains it. Landing on <main> left the inquiry
        // grid another 49 tab stops away — past the whole sidebar, top bar, view
        // rail and toolbar. So: hand focus to the screen's primary object when it
        // declares one (`role=grid` on every list screen, `data-skip-target`
        // otherwise) and fall back to the region when it does not.
        onClick={(event) => {
          const main = document.getElementById('af-main');
          const target =
            main?.querySelector<HTMLElement>('[data-skip-target],[role="grid"]') ?? main;
          if (!target) return;
          event.preventDefault();
          target.focus();
          target.scrollIntoView({ block: 'nearest' });
        }}
        className={[
          'sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-[var(--z-toast)]',
          'focus:rounded-[var(--radius-sm)] focus:border focus:border-line focus:bg-[var(--surface-overlay)]',
          'focus:px-3 focus:py-1.5 focus:text-base focus:text-primary focus:shadow-[var(--shadow-3)]',
        ].join(' ')}
      >
        Skip to content
      </a>

      {mobile ? (
        <NavDrawer open={navOpen} onOpenChange={setNavOpen} session={session} />
      ) : (
        <Sidebar collapsed={collapsed} session={session} />
      )}

      <div className="flex min-w-0 flex-1 flex-col bg-[var(--surface-1)]">
        <Topbar
          collapsed={mobile ? !navOpen : collapsed}
          mobile={mobile}
          onToggleSidebar={toggleSidebar}
          onOpenSearch={() => setPaletteOpen(true)}
          onOpenHelp={() => setHelpOpen(true)}
          onCreate={create}
          session={session}
        />

        {/* tabIndex -1 so the skip link's fallback target can actually take focus.
            `data-tour` is the onboarding's anchor — the tour spotlights the real
            control rather than a picture of one, so the four pieces of chrome it
            names carry the attribute here, in the shell, and work on every
            route. See `features/onboarding/TourOverlay`. */}
        <main
          id="af-main"
          tabIndex={-1}
          data-tour="main"
          className="relative min-h-0 flex-1 overflow-hidden outline-none"
        >
          <Suspense fallback={<ScreenSkeleton />}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} onCreate={create} />
      <ShortcutSheet open={helpOpen} onOpenChange={setHelpOpen} />
      {/* The third global overlay, and the only one that opens itself for
          nobody: it renders null until somebody asks for the tour. */}
      <TourOverlay />
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   Loading chrome
   ──────────────────────────────────────────────────────────────────────────── */

/** Column widths chosen to match the real inbox table, so nothing shifts. */
const SKELETON_COLUMNS = [56, 240, 132, 120, 26, 96, 40, 64, 72];

/**
 * Which fallback each route's geometry wants, in the order a path must be
 * tested. The router already gives every *chunk* its own skeleton; this is the
 * same map for the two moments the router cannot reach — the session probe in
 * front of it (`AppShellSkeleton`) and the shell's own `<Outlet/>` boundary.
 *
 * Without it, `/board` painted a full-width TABLE — 14 rows, 8 pseudo-columns,
 * zero column tracks — for the length of `/auth/me`, and only then swapped to
 * the board's own (correct) five-track skeleton. The first paint was the wrong
 * shape on the surface that is least like a table in the product.
 */
const ROUTE_SKELETON: Array<[string, () => ReactElement]> = [
  ['/getting-started', () => <GettingStartedSkeleton />],
  ['/board', () => <BoardScreenSkeleton />],
  ['/analytics', () => <AnalyticsScreenSkeleton />],
  ['/tasks', () => <TasksScreenSkeleton />],
  ['/settings', () => <SettingsScreenSkeleton />],
];

/** `/contacts/abc` is a record; `/contacts` is a list. */
const RECORD_DETAIL = /^\/(?:contacts|companies)\/[^/]+/;

/**
 * The screen-level placeholder. Row height is `--h-row` exactly and the
 * dividers sit where the table's do, so the swap to real data moves nothing.
 *
 * The generic shape below is the inbox/contacts/companies table; every route
 * whose geometry is not that table routes to its own fallback rather than
 * painting nine columns that are about to vanish.
 */
export function ScreenSkeleton({ rows = 14 }: { rows?: number }) {
  const { pathname } = useLocation();
  if (RECORD_DETAIL.test(pathname)) return <RecordDetailSkeleton />;
  const matched = ROUTE_SKELETON.find(([prefix]) => pathname.startsWith(prefix));
  if (matched) return matched[1]();

  return (
    <div className="flex h-full flex-col" aria-busy="true" aria-label="Loading">
      <div className="flex h-11 shrink-0 items-center gap-2 border-b border-line-subtle px-4">
        <Skeleton w={128} h={12} rounded="xs" />
        <span className="flex-1" />
        <Skeleton w={72} h={26} rounded="sm" />
        <Skeleton w={96} h={26} rounded="sm" />
        <Skeleton w={26} h={26} rounded="sm" />
      </div>

      <div className="flex h-8 shrink-0 items-center gap-4 border-b border-line px-4">
        {SKELETON_COLUMNS.map((width, index) => (
          <Skeleton key={index} w={Math.min(width, 72)} h={8} rounded="xs" />
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-hidden">
        {Array.from({ length: rows }, (_, row) => (
          <div
            key={row}
            className="flex items-center gap-4 border-b border-line-subtle px-4"
            style={{ height: 'var(--h-row)' }}
          >
            {SKELETON_COLUMNS.map((width, column) => (
              <Skeleton
                key={column}
                w={width}
                h={10}
                rounded="xs"
                style={{ opacity: 1 - row * 0.045 }}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * The whole frame in skeleton, for the moment before the session resolves.
 *
 * `ScreenSkeleton` reads the matched path, so the body of this frame is the
 * geometry of the route being loaded rather than a table on every surface.
 */
export function AppShellSkeleton() {
  /* The rail is a drawer below the breakpoint, and a drawer is closed until it
     is opened — painting 232px of it here would be a placeholder for something
     the reader is never going to see arrive. */
  const mobile = useIsMobile();
  return (
    <div className="flex h-full w-full overflow-hidden bg-[var(--canvas)]">
      {mobile ? null : <SidebarSkeleton />}
      <div className="flex min-w-0 flex-1 flex-col bg-[var(--surface-1)]">
        <TopbarSkeleton />
        <div className="min-h-0 flex-1 overflow-hidden">
          <ScreenSkeleton />
        </div>
      </div>
    </div>
  );
}
